import {
  LookbookSettingsCriteriaPage,
  LookbookSettingsGeneralPage,
  LookbookSettingsNavigationPage,
} from '@app/components/lookbook/LookbookSettingsPage';
import { Navigate, Route, Routes as ReactRoutes } from 'react-router-dom';

import { AccountRequestDetailsPage } from '../pages/AccountRequestDetailsPage/AccountRequestDetailsPage';
import { AccountRequestsList } from '../pages/AccountRequestsList';
import { LookbookCmsPage } from '../pages/LookbookCmsPage/LookbookCmsPage';
import { LookbookWishlistPage } from '../pages/LookbookWishlistPage/LookbookWishlistPage';
import { compiledPaths, paths } from '../paths';
import { AuthenticatedPath } from '../routesWrappingComponents/AuthenticatedPath';
import { OptionalAuthenticationPath } from '../routesWrappingComponents/OptionalAuthenticationPath';
import { SelectionLoader } from '../routesWrappingComponents/SelectionLoader';
import { LastSelectionLoader } from '../routesWrappingComponents/SelectionLoader/LastSelectionLoader';
import { UnauthenticatedPath } from '../routesWrappingComponents/UnauthenticatedPath';

import { AccountInfo } from './account/AccountInfo';
import { AccountPassword } from './account/AccountPassword';
import { AccountDetails } from './accounts/AccountDetails';
import { AccountList } from './accounts/AccountList';
import { LinkExpiredPage } from './authPage/LinkExpiredPage';
import { Checkout } from './checkout/Checkout';
import { CheckoutFailure, CheckoutPaymentFailure } from './checkout/CheckoutFailure';
import { CheckoutSuccess } from './checkout/CheckoutSuccess';
import { PaymentPending } from './checkout/PaymentPending';
import { PaymentResult } from './checkout/PaymentResult';
import { InvoicePaymentSuccessPage } from './invoices/InvoicePaymentSuccessPage';
import { InvoicePendingPage } from './invoices/InvoicePendingPage';
import { Invoices } from './invoices/Invoices';
import { ProcessingPaymentPage } from './invoices/ProcessingPaymentPage';
import { Linesheets } from './linesheets/Linesheets';
import { LookbookProductsPage } from './lookbook/LookbookProductsPage';
import { LookbooksPage } from './lookbook/LookbooksPage';
import { OrderDetailsPage } from './orders/OrderDetailsPage';
import { Orders } from './orders/Orders';
import { Selections } from './orders/Selections';
import { CmsPage } from './pages/page';
import { PageRoot } from './pages/page/PageRoot';
import { PagesList } from './pagesList/PagesList';
import { AllProductsPage } from './products/AllProductsPage';
import { LookbookProductDetailsPage } from './products/ProductDetails/LookbookProductDetailsPage';
import { CustomizationSettings } from './settings/customization/CustomizationSettings';
import { NavigationSettings } from './settings/navigation/NavigationSettings';
import { TranslationsSettings } from './settings/translations';
import { RedirectOrderSheet } from './various/Redirects';
import { ChangePasswordPage, ForgotPasswordPage, LoginPage, WelcomePage } from './authPage';
import { Cms, CmsPreview } from './cms';
import { Home } from './home';
import { InvoiceDetailsPage } from './invoices';
import { NoMatch } from './NoMatch';
import { Template } from './Template';

export const Routes = () => (
  <Template>
    <ReactRoutes>
      <Route
        path={paths.LOGIN}
        element={
          <UnauthenticatedPath>
            <LoginPage />
          </UnauthenticatedPath>
        }
      />

      <Route
        path={paths.PASSWORD_FORGOT}
        element={
          <UnauthenticatedPath>
            <ForgotPasswordPage />
          </UnauthenticatedPath>
        }
      />

      <Route
        path={paths.PASSWORD_CHANGE}
        element={
          <UnauthenticatedPath>
            <ChangePasswordPage />
          </UnauthenticatedPath>
        }
      />

      <Route
        path={paths.WELCOME}
        element={
          <UnauthenticatedPath>
            <WelcomePage />
          </UnauthenticatedPath>
        }
      />

      <Route
        path={paths.PAGES_LIST}
        element={
          <AuthenticatedPath limitAccess={['admin']}>
            <PagesList />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.PAGE_ROOT}
        element={
          <AuthenticatedPath>
            <PageRoot />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.PAGE_ORDER}
        element={
          <AuthenticatedPath>
            <SelectionLoader>
              <CmsPage />
            </SelectionLoader>
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.CMS_PREVIEW}
        element={
          <AuthenticatedPath limitAccess={['admin']} hideHeader>
            <CmsPreview />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.CMS_EDITOR}
        element={
          <AuthenticatedPath limitAccess={['admin']} hideHeader>
            <Cms />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ACCOUNT}
        element={
          <AuthenticatedPath>
            <Navigate to={paths.ACCOUNT_INFO} replace />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ACCOUNT_INFO}
        element={
          <AuthenticatedPath>
            <AccountInfo />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ACCOUNT_PASSWORD}
        element={
          <AuthenticatedPath>
            <AccountPassword />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ACCOUNTS}
        element={
          <AuthenticatedPath limitAccess={['admin', 'seller']}>
            <AccountList />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ACCOUNT_REQUESTS}
        element={
          <AuthenticatedPath limitAccess={['admin', 'seller']}>
            <AccountRequestsList />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ACCOUNT_REQUEST_DETAILS}
        element={
          <AuthenticatedPath limitAccess={['admin', 'seller']}>
            <AccountRequestDetailsPage />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ACCOUNT_DETAILS}
        element={
          <AuthenticatedPath limitAccess={['admin', 'seller']}>
            <AccountDetails />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.CHECKOUT_ORDER}
        element={
          <AuthenticatedPath>
            <SelectionLoader>
              <Checkout />
            </SelectionLoader>
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.CHECKOUT_FAILURE}
        element={
          <AuthenticatedPath>
            <CheckoutFailure />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.CHECKOUT_SUCCESS}
        element={
          <AuthenticatedPath>
            <SelectionLoader>
              <CheckoutSuccess />
            </SelectionLoader>
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.LOOKBOOK}
        element={
          <AuthenticatedPath limitAccess={['admin', 'seller']}>
            <LookbooksPage />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.LOOKBOOK_WISHLIST_PAGE}
        element={
          <OptionalAuthenticationPath hideHeader>
            <LookbookWishlistPage />
          </OptionalAuthenticationPath>
        }
      />

      <Route
        path={paths.LOOKBOOK_SINGLE}
        element={
          <OptionalAuthenticationPath hideHeader>
            <LookbookProductsPage />
          </OptionalAuthenticationPath>
        }
      />
      <Route
        path={paths.LOOKBOOK_CMS_PAGE}
        element={
          <OptionalAuthenticationPath hideHeader>
            <LookbookCmsPage />
          </OptionalAuthenticationPath>
        }
      />

      <Route
        path={paths.LOOKBOOK_SINGLE_PRODUCT}
        element={
          <OptionalAuthenticationPath>
            <LookbookProductDetailsPage />
          </OptionalAuthenticationPath>
        }
      />

      <Route
        path={paths.LOOKBOOK_SINGLE_SETTINGS_GENERAL}
        element={
          <AuthenticatedPath limitAccess={['admin', 'seller']}>
            <LookbookSettingsGeneralPage />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.LOOKBOOK_SINGLE_SETTINGS_NAVIGATION}
        element={
          <AuthenticatedPath limitAccess={['admin', 'seller']}>
            <LookbookSettingsNavigationPage />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.LOOKBOOK_SINGLE_SETTINGS_CRITERIA}
        element={
          <AuthenticatedPath limitAccess={['admin', 'seller']}>
            <LookbookSettingsCriteriaPage />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ORDERSHEETS}
        element={
          <AuthenticatedPath>
            <RedirectOrderSheet />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.SELECTIONS}
        element={
          <AuthenticatedPath>
            <Selections />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ORDERS}
        element={
          <AuthenticatedPath>
            <Orders />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ORDER_DETAILS}
        element={
          <AuthenticatedPath>
            <SelectionLoader>
              <OrderDetailsPage />
            </SelectionLoader>
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.PRODUCTS_ROOT}
        element={
          <AuthenticatedPath>
            <LastSelectionLoader getRedirectionUrl={id => compiledPaths.PRODUCTS_ORDER({ id })} />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.PRODUCTS_ORDER}
        element={
          <AuthenticatedPath>
            <SelectionLoader>
              <AllProductsPage />
            </SelectionLoader>
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.HOME_ROOT}
        element={
          <AuthenticatedPath>
            <LastSelectionLoader getRedirectionUrl={id => compiledPaths.HOME({ id })} />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.HOME}
        element={
          <AuthenticatedPath>
            <SelectionLoader>
              <Home />
            </SelectionLoader>
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.INVOICES}
        element={
          <AuthenticatedPath limitAccess={['buyer']}>
            <Invoices />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.INVOICE_DETAILS}
        element={
          <AuthenticatedPath>
            <InvoiceDetailsPage />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.INVOICE_PAYMENT_PENDING}
        element={
          <AuthenticatedPath>
            <InvoicePendingPage />
          </AuthenticatedPath>
        }
      />
      <Route
        path={paths.INVOICE_PAYMENT_PROCESSING}
        element={
          <AuthenticatedPath>
            <ProcessingPaymentPage />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.INVOICE_PAYMENT_SUCCESS}
        element={
          <AuthenticatedPath>
            <InvoicePaymentSuccessPage />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ORDER_PAYMENT_RESULT}
        element={
          <AuthenticatedPath>
            <PaymentResult />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ORDER_PAYMENT_FAILURE}
        element={
          <AuthenticatedPath>
            <CheckoutPaymentFailure />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ORDER_PAYMENT_PENDING}
        element={
          <AuthenticatedPath>
            <PaymentPending />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.ROOT}
        element={
          <AuthenticatedPath limitAccess={['admin', 'seller']} fallbackPath={compiledPaths.HOME_ROOT({})}>
            <Navigate to={paths.SELECTIONS} replace />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.SETTINGS}
        element={
          <AuthenticatedPath limitAccess={['admin']}>
            <Navigate to={paths.SETTINGS_CUSTOMIZATION} replace />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.SETTINGS_CUSTOMIZATION}
        element={
          <AuthenticatedPath limitAccess={['admin']}>
            <CustomizationSettings />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.SETTINGS_NAVIGATION}
        element={
          <AuthenticatedPath limitAccess={['admin']}>
            <NavigationSettings />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.TRANSLATIONS}
        element={
          <AuthenticatedPath limitAccess={['admin']}>
            <Navigate to={paths.SETTINGS_TRANSLATIONS} replace />
          </AuthenticatedPath>
        }
      />
      <Route
        path={paths.SETTINGS_TRANSLATIONS}
        element={
          <AuthenticatedPath limitAccess={['admin']}>
            <TranslationsSettings />
          </AuthenticatedPath>
        }
      />

      <Route
        path={paths.LINESHEETS}
        element={
          <AuthenticatedPath limitAccess={['admin', 'seller']}>
            <Linesheets />
          </AuthenticatedPath>
        }
      />

      <Route path={paths.LINK_EXPIRED} element={<LinkExpiredPage />} />
      <Route path="*" element={<NoMatch />} />
    </ReactRoutes>
  </Template>
);
